// Price movement is separate from account return and contract dollar value.
// The old `pips` storage key remains readable for existing journals/backups.
const currencies = new Set('USD EUR GBP JPY CHF CAD AUD NZD SGD NOK SEK DKK HKD CNH CNY MXN ZAR TRY PLN HUF CZK'.split(' '));
const futures = /^(?:MNQ|MES|MGC|MYM|M2K|MCL|MNG|MSI|MHG|NQ|ES|GC|YM|RTY|CL|NG|SI|HG|ZB|ZN|ZF|ZT|ZC|ZW|ZS|6A|6B|6C|6E|6J|6N|6S|M6A|M6B|M6E)(?:[12]!|=F|[FGHJKMNQUVXZ]\d{1,4})?$/;
const legacyPoints = /(?:NAS|SPX|US30|DAX|FTSE|BTC|ETH|XAU|XAG)/;

export function instrumentMovement(instrument) {
  const symbol = String(instrument || '').trim().toUpperCase().split(':').at(-1).replace(/\s/g, '');
  if (futures.test(symbol.replace(/[-_]/g, ''))) return {unit:'points', label:'Points', pipSize:1, legacyPoints:false};
  const pair = symbol.replace(/[/-]/g, '').match(/^([A-Z]{3})([A-Z]{3})(?:[._][A-Z0-9]+|M|PRO|RAW)?$/);
  if (pair && currencies.has(pair[1]) && currencies.has(pair[2]) && pair[1] !== pair[2]) {
    return {unit:'pips', label:'Pips', pipSize:pair[2] === 'JPY' ? .01 : .0001, legacyPoints:false};
  }
  return {unit:'points', label:'Points', pipSize:1, legacyPoints:legacyPoints.test(symbol)};
}

export function priceNumber(value) {
  if (value == null || typeof value === 'boolean') return null;
  let text = String(value).trim();
  if (/^[+-]?\d{1,3}(?:,\d{3})+(?:\.\d+)?$/.test(text)) text = text.replace(/,/g, '');
  if (!/^[+-]?(?:\d+(?:\.\d*)?|\.\d+)(?:e[+-]?\d+)?$/i.test(text)) return null;
  const parsed = Number(text);
  return Number.isFinite(parsed) ? parsed : null;
}

export function calculatePriceMovement(entry, exit, direction, instrument) {
  const start = priceNumber(entry), end = priceNumber(exit);
  if (start == null || end == null || !['LONG','SHORT'].includes(direction) || !String(instrument || '').trim()) return null;
  const amount = (direction === 'LONG' ? end - start : start - end) / instrumentMovement(instrument).pipSize;
  if (!Number.isFinite(amount)) return null;
  // Preserve quarter-point futures and fractional pips without binary noise.
  const rounded = Number(amount.toFixed(8));
  return Object.is(rounded, -0) ? 0 : rounded;
}

export function tradeMovement(trade = {}) {
  const market = instrumentMovement(trade.instrument);
  const calculated = calculatePriceMovement(trade.entry, trade.closePrice, trade.direction, trade.instrument);
  if (calculated != null) return {...market, value:calculated};
  const stored = priceNumber(trade.pips);
  // Unmarked futures values were calculated with the forex multiplier. Without
  // execution prices there is no safe conversion: keep the original in records,
  // but don't present an invented points figure in the UI/readable exports.
  const trusted = trade.movementUnit === market.unit || market.unit === 'pips' || market.legacyPoints;
  return {...market, value:trusted ? stored : null};
}

export function formatMovement(value, {signed = true} = {}) {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${signed && value >= 0 ? '+' : ''}${value.toLocaleString('en-GB', {maximumFractionDigits:8})}`;
}
